import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { api } from '@/api/client';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Tag } from '@/components/ui/Badge';
import { buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { SegmentedControl, Tabs } from '@/components/ui/Navigation';
import { PageHeader } from '@/components/ui/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { EmptyState, SkeletonCard } from '@/components/ui/States';
import {
  PRACTICE_SECTIONS,
  PRACTICE_SECTION_META,
  formatBlueprintTitle,
  formatDuration,
  isPracticeSection,
  isStudentVisibleBlueprint,
  skillMatchesSection,
  type PracticeSection,
} from '@/features/practice/practice-sections';
import { DrillCard } from '@/features/practice/DrillCard';
import { MockCard } from '@/features/practice/MockCard';
import { statsText, takenStats, type AttemptRow } from '@/features/practice/mock-stats';
import { PlanRequiredModal } from '@/features/practice/PlanRequiredModal';
import { PracticeStartButton } from '@/features/practice/PracticeStartButton';
import {
  canAccessPractice,
  getPracticeAccessReason,
  type PracticeAccessReason,
} from '@/features/practice/practice-access';
import { usePreTestGate } from '@/features/practice/use-pre-test-gate';
import { useStartAttempt } from '@/features/practice/use-start-attempt';
import { summarizeBlueprintSlots } from '@/features/reading/ReadingSection';
import { useAccess } from '@/features/billing/use-access';
import { filterPublicExams } from '@/lib/catalog-visibility';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/practice')({
  validateSearch: (search: Record<string, unknown>) => ({
    variant: typeof search['variant'] === 'string' ? search['variant'] : undefined,
    section: isPracticeSection(search['section']) ? search['section'] : undefined,
    ...(search['view'] === 'types' ? { view: 'types' as const } : {}),
  }),
  component: PracticePage,
});

interface BlueprintRow {
  slug: string;
  version: number;
  kind: string;
  skill: string | null;
  definition: unknown;
  sectionSummary: {
    skill: string;
    delivery: 'free' | 'sequential';
    timeSeconds: number;
    itemCount: number | null;
  }[];
  totalTimeSeconds: number;
  drill: { taskType: string; baseCount: number; allowsCount: boolean } | null;
}

function skillsOf(blueprint: BlueprintRow): string[] {
  return blueprint.sectionSummary.map((row) => row.skill);
}

function formatSectionChips(blueprint: BlueprintRow): string[] {
  return blueprint.sectionSummary.map((section) => {
    const itemLabel = section.itemCount === null ? 'items vary' : `${section.itemCount} items`;
    return `${section.skill} · ${formatDuration(section.timeSeconds)} · ${itemLabel}`;
  });
}

function PracticePage() {
  const navigate = useNavigate();
  const { variant: variantSearch, section: sectionSearch, view: viewSearch } = Route.useSearch();
  const { start, isPending, pendingKey, errorMessage, clearError } = useStartAttempt();
  const gate = usePreTestGate(start);
  const { data: session } = authClient.useSession();
  const access = useAccess(Boolean(session));
  const [planModalReason, setPlanModalReason] = useState<Exclude<
    PracticeAccessReason,
    'allowed'
  > | null>(null);

  const examsQuery = useQuery({
    queryKey: ['exams'],
    queryFn: () => api.get('/api/v1/exams'),
  });

  const selectedVariant = useMemo(() => {
    const exams = filterPublicExams(examsQuery.data?.exams ?? []);
    if (variantSearch) {
      for (const exam of exams) {
        const match = exam.variants.find((row) => row.slug === variantSearch);
        if (match) return { exam, variant: match };
      }
    }
    const firstExam = exams[0];
    const firstVariant = firstExam?.variants[0];
    if (!firstExam || !firstVariant) return null;
    return { exam: firstExam, variant: firstVariant };
  }, [examsQuery.data, variantSearch]);

  const activeSection: PracticeSection = sectionSearch ?? 'mocks';

  const blueprintsQuery = useQuery({
    queryKey: ['blueprints', selectedVariant?.variant.slug],
    enabled: Boolean(selectedVariant),
    queryFn: () =>
      api.get('/api/v1/exams/{variant}/blueprints', {
        params: { variant: selectedVariant!.variant.slug },
      }),
  });

  const visibleBlueprints = useMemo(() => {
    const rows = (blueprintsQuery.data?.blueprints ?? []) as BlueprintRow[];
    return rows.filter((row) => isStudentVisibleBlueprint(row.slug));
  }, [blueprintsQuery.data]);

  const counts = useMemo(() => {
    const next: Record<PracticeSection, number> = {
      mocks: 0,
      listening: 0,
      reading: 0,
      writing: 0,
      speaking: 0,
    };
    for (const row of visibleBlueprints) {
      if (row.kind === 'mock') {
        next.mocks += 1;
        continue;
      }
      if (row.kind !== 'practice') continue;
      for (const skill of ['listening', 'reading', 'writing', 'speaking'] as const) {
        if (skillMatchesSection(row.skill, skill)) next[skill] += 1;
      }
    }
    return next;
  }, [visibleBlueprints]);

  const attemptsQuery = useQuery({
    queryKey: ['attempts', 'for-cards'],
    enabled: Boolean(session),
    queryFn: () => api.get('/api/v1/attempts', { query: { limit: '100' } }),
  });
  const attemptRows: AttemptRow[] = attemptsQuery.data?.attempts ?? [];

  const taskTypesQuery = useQuery({
    queryKey: ['task-types', selectedVariant?.variant.slug],
    enabled: Boolean(selectedVariant),
    queryFn: () =>
      api.get('/api/v1/exams/{variant}/task-types', {
        params: { variant: selectedVariant?.variant.slug ?? '' },
      }),
  });

  const inSection = useMemo(() => {
    if (activeSection === 'mocks') {
      return visibleBlueprints.filter((row) => row.kind === 'mock');
    }
    return visibleBlueprints.filter(
      (row) => row.kind === 'practice' && skillMatchesSection(row.skill, activeSection),
    );
  }, [visibleBlueprints, activeSection]);

  const drillRows = inSection.filter((row) => row.drill !== null);
  const hasDrills = activeSection !== 'mocks' && drillRows.length > 0;
  const showDrills = hasDrills && viewSearch === 'types';
  const filtered = showDrills
    ? drillRows
    : hasDrills
      ? inSection.filter((row) => !row.drill)
      : inSection;

  if (examsQuery.isPending) {
    return (
      <div className="grid gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (examsQuery.isError || !selectedVariant) {
    return (
      <Alert tone="danger" title="We could not load the practice catalogue.">
        Reload the page to try again.
      </Alert>
    );
  }

  const exams = filterPublicExams(examsQuery.data?.exams ?? []);
  const sectionMeta = PRACTICE_SECTION_META[activeSection];
  const variantOptions = exams.flatMap((exam) =>
    exam.variants.map((variant) => ({
      value: variant.slug,
      label: exam.name === variant.name ? variant.name : `${exam.name} · ${variant.name}`,
    })),
  );

  const accessInput = {
    isLoggedIn: Boolean(session),
    canStart: access.data?.canStart,
    emailVerified: access.data?.emailVerified,
  };

  const practiceLocked =
    access.isPending && Boolean(session) ? true : !canAccessPractice(accessInput);

  function showPlanRequired() {
    const reason = getPracticeAccessReason(accessInput);
    if (reason !== 'allowed') setPlanModalReason(reason);
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow={selectedVariant.variant.name}
        title={sectionMeta.label}
        description={sectionMeta.description}
        actions={
          variantOptions.length > 1 ? (
            <SegmentedControl
              label="Exam variant"
              value={selectedVariant.variant.slug}
              onChange={(value) =>
                void navigate({
                  to: '/practice',
                  search: { variant: value, section: activeSection },
                })
              }
              items={variantOptions}
            />
          ) : null
        }
      />

      <ExamSurfaceDisclaimer text={selectedVariant.exam.trademarkDisclaimer} />

      <Tabs
        label="Practice sections"
        value={activeSection}
        onChange={(value) =>
          void navigate({
            to: '/practice',
            search: { variant: selectedVariant.variant.slug, section: value },
          })
        }
        items={PRACTICE_SECTIONS.map((section) => ({
          value: section,
          label: `${PRACTICE_SECTION_META[section].label} (${counts[section]})`,
        }))}
      />

      {hasDrills ? (
        <SegmentedControl
          label="How to practise"
          value={showDrills ? 'types' : 'sections'}
          onChange={(value) =>
            void navigate({
              to: '/practice',
              search: {
                variant: selectedVariant.variant.slug,
                section: activeSection,
                ...(value === 'types' ? { view: 'types' as const } : {}),
              },
            })
          }
          items={[
            { value: 'sections', label: 'Whole sections' },
            { value: 'types', label: 'By question type' },
          ]}
        />
      ) : null}

      {errorMessage ? (
        <Alert tone="danger" title={errorMessage}>
          <button
            type="button"
            className="font-bold underline"
            onClick={() => {
              clearError();
            }}
          >
            Dismiss
          </button>
        </Alert>
      ) : null}

      {blueprintsQuery.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={sectionMeta.icon}
          title={`No ${sectionMeta.label.toLowerCase()} yet`}
          description="There is nothing here for this exam variant at the moment. Try another section."
          action={
            <Link to="/pricing" className={buttonClassName({ variant: 'secondary' })}>
              See plans
            </Link>
          }
        />
      ) : (
        <ul className={activeSection === 'mocks' ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
          {filtered.map((blueprint) =>
            activeSection === 'mocks' ? (
              <MockCard
                key={`${blueprint.slug}-${blueprint.version}`}
                title={formatBlueprintTitle(blueprint.slug)}
                slug={blueprint.slug}
                modules={blueprint.sectionSummary.map((row) => ({
                  skill: row.skill,
                  timeSeconds: row.timeSeconds,
                }))}
                attempts={attemptRows}
                locked={practiceLocked}
                onLockedClick={showPlanRequired}
                pendingKey={isPending ? pendingKey : null}
                disabled={isPending}
                onStart={(sections, key) => {
                  gate.request({
                    skills: sections ?? skillsOf(blueprint),
                    variant: selectedVariant.variant.slug,
                    blueprintSlug: blueprint.slug,
                    mode: 'test',
                    key,
                    ...(sections === undefined ? {} : { sections }),
                  });
                }}
              />
            ) : showDrills && blueprint.drill ? (
              <DrillCard
                key={`${blueprint.slug}-${blueprint.version}`}
                label={
                  taskTypesQuery.data?.taskTypes.find((row) => row.id === blueprint.drill?.taskType)
                    ?.label ?? formatBlueprintTitle(blueprint.slug)
                }
                baseCount={blueprint.drill.baseCount}
                allowsCount={blueprint.drill.allowsCount}
                baseSeconds={blueprint.totalTimeSeconds}
                locked={practiceLocked}
                onLockedClick={showPlanRequired}
                loading={pendingKey === `${blueprint.slug}:drill` && isPending}
                disabled={isPending}
                onStart={(itemCount) => {
                  gate.request({
                    skills: skillsOf(blueprint),
                    variant: selectedVariant.variant.slug,
                    blueprintSlug: blueprint.slug,
                    key: `${blueprint.slug}:drill`,
                    preferUnseen: true,
                    ...(itemCount === undefined ? {} : { itemCount }),
                  });
                }}
              />
            ) : (
              <Card
                key={`${blueprint.slug}-${blueprint.version}`}
                as="li"
                padding="lg"
                className="flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="min-w-0 font-display text-h4 text-ink">
                    {formatBlueprintTitle(blueprint.slug)}
                  </h3>
                  <Tag tone={blueprint.kind === 'mock' ? 'ink' : 'neutral'}>
                    {blueprint.kind === 'mock' ? 'Mock test' : (blueprint.skill ?? activeSection)}
                  </Tag>
                </div>

                <p className="text-body text-ink-2">
                  {summarizeBlueprintSlots(blueprint.definition) ||
                    formatSectionChips(blueprint).join(' · ') ||
                    'Practice section'}
                </p>

                <p className="flex items-center gap-1.5 text-small text-muted">
                  <Icon icon={Clock} size={16} />
                  {formatDuration(blueprint.totalTimeSeconds)} ·{' '}
                  {statsText(
                    takenStats(attemptRows, blueprint.slug).taken,
                    takenStats(attemptRows, blueprint.slug).best,
                  )}
                </p>

                <div className="mt-auto flex flex-wrap gap-2">
                  {activeSection === 'listening' ? (
                    <>
                      <PracticeStartButton
                        size="sm"
                        locked={practiceLocked}
                        onLockedClick={showPlanRequired}
                        loading={pendingKey === `${blueprint.slug}:test` && isPending}
                        disabled={isPending}
                        onClick={() => {
                          start({
                            variant: selectedVariant.variant.slug,
                            blueprintSlug: blueprint.slug,
                            mode: 'test',
                            key: `${blueprint.slug}:test`,
                          });
                        }}
                      >
                        Start under test conditions
                      </PracticeStartButton>
                      <PracticeStartButton
                        size="sm"
                        variant="secondary"
                        locked={practiceLocked}
                        onLockedClick={showPlanRequired}
                        loading={pendingKey === `${blueprint.slug}:practice` && isPending}
                        disabled={isPending}
                        onClick={() => {
                          start({
                            variant: selectedVariant.variant.slug,
                            blueprintSlug: blueprint.slug,
                            mode: 'practice',
                            key: `${blueprint.slug}:practice`,
                          });
                        }}
                      >
                        Practise without a timer
                      </PracticeStartButton>
                    </>
                  ) : (
                    <PracticeStartButton
                      size="sm"
                      locked={practiceLocked}
                      onLockedClick={showPlanRequired}
                      loading={pendingKey === `${blueprint.slug}:default` && isPending}
                      disabled={isPending}
                      onClick={() => {
                        gate.request({
                          skills: skillsOf(blueprint),
                          variant: selectedVariant.variant.slug,
                          blueprintSlug: blueprint.slug,
                          key: `${blueprint.slug}:default`,
                        });
                      }}
                    >
                      Start
                    </PracticeStartButton>
                  )}
                </div>
              </Card>
            ),
          )}
        </ul>
      )}

      {gate.modal}

      {planModalReason ? (
        <PlanRequiredModal
          open
          reason={planModalReason}
          onClose={() => {
            setPlanModalReason(null);
          }}
        />
      ) : null}
    </div>
  );
}
